import * as React from 'react';
import { Box } from '@mui/material';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import QueryValuesLayout from 'src/components/Layouts/QueryValuesLayout';
import Paper from 'src/components/Paper';
import { AppsListWrapper } from './components/AppsListWrapper';
import { AppCreateModal } from './components/AppCreateModal';
import { App, CreateAppPayload, UpdateAppPayload } from 'src/dto/App';
import { GetListLimit } from 'src/dto/Limits';
import { useCreateAppMutation, useUpdateAppMutation } from 'src/redux/services/apps';
import SnackBarCustom from 'src/frontend-common/components/SnackbarCustom';
import ButtonComponent from 'src/components/ButtonComponent';
import { getActiveCompany } from 'src/frontend-common/localstorage';

interface State {
  search: string | null;
}

interface Props {
  limit: GetListLimit;
  setLimit: (s: GetListLimit) => void;
  page: number;
  setPage: (s: number) => void;
}

const nameFilterInitialValue = null;

const AppsPage: React.FC<Props> = ({ page, setPage, limit, setLimit }) => {
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [selectedApp, setSelectedApp] = React.useState<App | null>(null);
  const [showSnackBar, setShowSnackBar] = React.useState(false);
  const [currentSelectedCompany, setCurrentSelectedCompany] = React.useState<string>('');

  // Filters
  const [search, setSearch] = React.useState<State['search']>(nameFilterInitialValue);

  const [createApp, createdApp] = useCreateAppMutation();
  const [updateApp, updatedApp] = useUpdateAppMutation();

  const activeCompany = getActiveCompany();

  React.useEffect(() => {
    if (activeCompany) {
      // search for the first two letters of the active company name
      setCurrentSelectedCompany(activeCompany.id);
    }
  }, [activeCompany]);

  React.useEffect(() => {
    if (updatedApp.isSuccess || updatedApp.isError) {
      setShowSnackBar(true);
    }

    if (updatedApp.isSuccess) {
      setIsModalOpen(false);
    }
  }, [updatedApp]);

  React.useEffect(() => {
    if (createdApp.isSuccess || createdApp.isError) {
      setShowSnackBar(true);
    }

    if (createdApp.isSuccess) {
      setIsModalOpen(false);
    }
  }, [createdApp]);

  const handleEdit = (app: App) => {
    setSelectedApp(app);
    setIsModalOpen(true);
  };

  const handleNewAppClick = () => {
    setIsModalOpen(true);
    setSelectedApp(null);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedApp(null);
  };

  const handleUpdate = (app: UpdateAppPayload) => {
    updateApp(app);
  };

  const handleCreate = (values: CreateAppPayload) => {
    createApp(values);
  };

  return (
    <DashboardPageLayout title="Apps" aside={<ButtonComponent text="Create App" buttontype="mainaction" onClick={() => handleNewAppClick()} />}>
      <AppCreateModal
        open={isModalOpen}
        onClose={handleClose}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        initData={selectedApp}
        activeCompany={currentSelectedCompany}
      />
      <Box>
        <Paper>
          <AppsListWrapper
            searchFilter={search}
            setSearchFilter={setSearch}
            limit={limit}
            page={page}
            setPage={setPage}
            setLimit={setLimit}
            onEdit={handleEdit}
          />
        </Paper>
      </Box>
      <SnackBarCustom
        open={showSnackBar}
        onClose={() => setShowSnackBar(false)}
        severity={createdApp.isSuccess || updatedApp.isSuccess ? 'success' : 'error'}
        message={
          createdApp.isSuccess || updatedApp.isSuccess ? 'Your info was correctly updated!' : 'There was a problem updating your info! Please try again.'
        }
      />
    </DashboardPageLayout>
  );
};

const WrappedAppManagementPage: React.FC = () => (
  <QueryValuesLayout excludeValues={['sort', 'dateRange', 'token', 'tab']}>
    {({ setLimit, page, setPage }) => <AppsPage limit={100} setLimit={setLimit} page={page} setPage={setPage} />}
  </QueryValuesLayout>
);
export { WrappedAppManagementPage as AppsPage };
